import { ComponentProps, useRef } from "react";
import { useNearViewport } from "../../../hooks/useNearViewport";
import { CloudinaryImage, CloudinaryImageUrl, forScreen } from "./Image";

type NearViewportCloudinaryImageProps = Omit<
  ComponentProps<typeof CloudinaryImage>,
  "layout" | "width" | "height"
> & {
  // How far outside the viewport the image starts loading. Native
  // `loading="lazy"` starts 1250px+ away, which pulls images below the fold
  // into the page load.
  loadMargin: string;
};

// A filling CloudinaryImage that is only rendered once it nears the viewport.
// Google can recognize these kind of images as SEO. Only browser can not handle js won't recognize like a Bing
// But Crawlers that don't run JavaScript also can get the image from <noscript>.
export function NearViewportCloudinaryImage({
  loadMargin,
  ...props
}: NearViewportCloudinaryImageProps) {
  const placeholderRef = useRef<HTMLSpanElement>(null);
  const isNearViewport = useNearViewport(placeholderRef, loadMargin);
  const noScriptSrc = CloudinaryImageUrl({
    src: forScreen(props.src, "web"),
    transformations: forScreen(props.transformations, "web"),
  });

  if (!props.isPriority && !isNearViewport) {
    // The image fills its container (CloudinaryImage uses `fill`), so the placeholder does too.
    return (
      <>
        <span
          ref={placeholderRef}
          style={{ position: "absolute", inset: 0 }}
          aria-hidden
        />
        <noscript>
          <img
            src={noScriptSrc}
            alt={props.alt}
            loading="lazy"
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </noscript>
      </>
    );
  }

  return <CloudinaryImage {...props} />;
}
